"use client";

import { PartzyncLogo } from "@/app/layout/PartzyncLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ChevronsUpDown, Store } from "lucide-react";
import { VERIFIED_LEVEL_LABELS } from "../lib/shop-verification";
import { useMerchantShops } from "../store/use-merchant-shops";

export function ShopSwitcher() {
  const { shops, isLoading } = useMerchantShops();
  const params = useParams({ strict: false });
  const currentShopId = params?.shopId as string | undefined;
  const navigate = useNavigate();
  const { isMobile } = useSidebar();

  const currentShop = currentShopId
    ? shops.find((s) => s.shopId === currentShopId)
    : shops[0];
  const singleShop = shops.length <= 1;

  if (isLoading || shops.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="opacity-70">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Store className="size-5" />
            </div>
            <div className="grid flex-1 text-start text-sm">
              <span className="truncate">ກຳລັງໂຫຼດ...</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const handleSelect = (shopId: string) => {
    navigate({ to: "/app/shops/$shopId", params: { shopId } });
  };

  const trigger = (
    <SidebarMenuButton
      size="lg"
      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
    >
      <div className="flex aspect-square size-8 items-center justify-center overflow-hidden rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
        <PartzyncLogo className="size-5" />
      </div>
      <div className="grid flex-1 text-start text-sm leading-tight">
        <span className="truncate font-semibold">
          {currentShop?.shopName ?? "ເລືອກລາຍການ"}
        </span>
        <span className="truncate text-sidebar-foreground/80 text-xs">
          {currentShop?.roleName ??
            (shops.length > 0 ? `${shops.length} ລາຍການ` : "")}
          {currentShop?.verifiedLevel && (
            <> · {VERIFIED_LEVEL_LABELS[currentShop.verifiedLevel]}</>
          )}
        </span>
      </div>
      {!singleShop && <ChevronsUpDown className="ms-auto size-4" />}
    </SidebarMenuButton>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {singleShop && currentShop ? (
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-3 px-2 py-1.5 text-left"
            onClick={() => handleSelect(currentShop.shopId)}
          >
            <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <PartzyncLogo className="size-8" />
            </div>
            <div className="grid min-w-0 flex-1 text-start text-sm leading-tight">
              <span className="truncate font-semibold">
                {currentShop.shopName}
              </span>
              <span className="truncate text-sidebar-foreground/80 text-xs">
                {currentShop.roleName}
              </span>
            </div>
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-56 rounded-lg"
              align="start"
              side={isMobile ? "bottom" : "right"}
              sideOffset={4}
            >
              {shops.map((shop) => (
                <DropdownMenuItem
                  key={shop.shopId}
                  onClick={() => handleSelect(shop.shopId)}
                  className="gap-2 p-2"
                >
                  <Store className="size-4 shrink-0" />
                  <div className="flex flex-col">
                    <span>{shop.shopName}</span>
                    <span className="text-muted-foreground text-xs">
                      {shop.roleName}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
